import { type FC, useEffect } from 'react';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useApiAdminapitokensIdDelete } from '@r/api/generated/client.ts';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type { ApiTokenApiTokenReadModel } from '@r/api/generated/client.schemas.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type Token = ApiTokenApiTokenReadModel;

interface ApiTokenToDeleteFormProps {
  tokenToDelete: Token | null;
  onSuccess: () => void;
}

export const ApiTokenToDeleteForm: FC<ApiTokenToDeleteFormProps> = ({
  tokenToDelete,
  onSuccess
}) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();

  const removeTokenMutation = useApiAdminapitokensIdDelete({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.api.token.delete.success'));
        onSuccess();
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('admin.api.token.delete.error'));
      },
      networkMode: 'always'
    }
  });

  const handleRemoveToken = () => {
    if (tokenToDelete) {
      removeTokenMutation.mutate({ id: tokenToDelete.id.toString() });
    }
  };

  const TokenToDeleteFormSchema = z.object({
    tokenToDeleteName: z.string().refine(
      (val) => {
        const normalizedName = val.replace(/\s+/g, ' ').trim();

        return tokenToDelete?.name === normalizedName;
      },
      {
        message: t('admin.api.token.delete.form.name.error')
      }
    )
  });

  const tokenToDeleteForm = useForm<z.infer<typeof TokenToDeleteFormSchema>>({
    resolver: zodResolver(TokenToDeleteFormSchema),
    defaultValues: {
      tokenToDeleteName: ''
    }
  });

  useEffect(() => {
    if (tokenToDelete) {
      tokenToDeleteForm.reset();
    }
  }, [tokenToDelete]);

  return (
    <Form {...tokenToDeleteForm}>
      <form onSubmit={tokenToDeleteForm.handleSubmit(handleRemoveToken)}>
        <FormField
          control={tokenToDeleteForm.control}
          name="tokenToDeleteName"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.api.token.delete.form.input.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('admin.api.token.delete.form.input.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button loading={removeTokenMutation.isPending} className="mt-5 w-full" type="submit">
          {t('admin.api.token.delete.form.button')}
        </Button>
      </form>
    </Form>
  );
};
